import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { ComputerService } from 'src/app/computer/computer.service';
import { ThermalPrintTargetService } from 'src/app/computer/thermal-print-target.service';
import { FiscalService } from 'src/app/fiscal/fiscal.service';
import { setLoading } from 'src/app/redux/actions/user.actions';
import { AppState } from 'src/app/redux/app.state';
import {
  Fiscal,
  LabelProfileRef,
  PrinterTarget,
  RingostatAvailabilityStatus,
  RingostatDestinationType,
  UpsertRingostatUserSettingsDto,
  User
} from 'src/typings';
import { NotificationService } from 'src/app/services/notification.service';
import { RingostatUserSettingsService } from '../ringostat-user-settings.service';
import { CurrentUserService as UserService } from '../user.service';

type PrinterTargetOption = {
  key: string;
  label: string;
  profileId?: number | string | null;
  profileCode?: string | null;
};

@Component({
  selector: 'app-setting',
  templateUrl: './setting.component.html',
  styleUrls: ['./setting.component.scss']
})
export class SettingComponent implements OnInit, OnDestroy {
  thermalAgentOptions: Array<{ agentName: string; label: string }> = [];
  labelProfileOptions: PrinterTargetOption[] = [];
  fiscalList: Fiscal[] = [];
  ringostatSaving = false;
  readonly ringostatDndMinuteOptions = [15, 30, 60, 120];
  private ringostatUserId: number | null = null;

  set loading(value: boolean) {
    this.store.dispatch(setLoading({ payload: value }));
  }

  storeSubscription: Subscription;

  set user(value: User | undefined) {
    if (!value) return;

    this._user = value;
    const {
      name,
      warehouse,
      org,
      therminal,
      labelProfile,
      thermalPrinter,
      fiscal,
      catalogueOpenFolder,
      catalogueOpenFilter
    } = this.userForm.controls;

    name.setValue(value.name);
    warehouse.setValue(value.defaultWarehouse);
    org.setValue(value.defaultOrg);
    therminal.setValue(value.defaultTherminal);
    labelProfile.setValue(this.getLabelProfileKey(value.defaultLabelProfile));
    thermalPrinter.setValue(value.defaultThermalPrinter);
    fiscal.setValue(value.fiscal || null);
    this.ensureFiscalOption(value.fiscal);
    catalogueOpenFolder.setValue(value.catalogueOpenFolder);
    catalogueOpenFilter.setValue(value.catalogueOpenFilter);

    if (this.ringostatUserId !== value.id) {
      this.ringostatUserId = value.id;
      void this.loadRingostatSettings(value.id);
    }
  }

  get orgControl() {
    return this.userForm.controls.org as UntypedFormControl;
  }

  get whControl() {
    return this.userForm.controls.warehouse as UntypedFormControl;
  }

  _user: User | undefined;

  get user() {
    return this._user;
  }

  constructor(
    private readonly titleService: Title,
    private readonly store: Store<AppState>,
    private readonly fb: UntypedFormBuilder,
    private readonly computerService: ComputerService,
    private readonly fiscalService: FiscalService,
    private readonly userService: UserService,
    private readonly thermalPrintTargetService: ThermalPrintTargetService,
    private readonly ringostatSettingsService: RingostatUserSettingsService,
    private readonly notificationService: NotificationService
  ) {
    this.titleService.setTitle('Настройки пользователя');
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      void this.loadPrinterTargets(data.user.info);
    });

    this.whControl.valueChanges.subscribe((warehouse) => {
      const warehouseId = (warehouse as { id?: number } | null | undefined)?.id;
      void this.loadPrinterTargets(this.user, warehouseId);
    });
  }

  ngOnInit() {
    this.loadFiscals();
  }

  userForm: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl(),
    warehouse: new UntypedFormControl(),
    org: new UntypedFormControl(),
    thermalPrinter: new UntypedFormControl(),
    labelProfile: new UntypedFormControl(null),
    therminal: new UntypedFormControl(),
    fiscal: new UntypedFormControl(),
    catalogueOpenFolder: new FormControl(false),
    catalogueOpenFilter: new FormControl(false),
    ringostatEnabled: new FormControl(false),
    ringostatDestinationType: new UntypedFormControl('sip'),
    ringostatDestination: new UntypedFormControl(''),
    ringostatSipLogin: new UntypedFormControl(''),
    ringostatMobileSipLogin: new UntypedFormControl(''),
    ringostatStaffId: new UntypedFormControl(''),
    ringostatFallbackPhone: new UntypedFormControl(''),
    ringostatDirectPhones: new UntypedFormControl(''),
    ringostatAvailabilityStatus: new UntypedFormControl('online'),
    ringostatDndMinutes: new UntypedFormControl(30)
  });

  ngOnDestroy() {
    this.storeSubscription.unsubscribe();
  }

  compareFiscal = (left?: Fiscal | null, right?: Fiscal | null) => {
    return Number(left?.id || 0) === Number(right?.id || 0);
  };

  save() {
    if (!this.user) return;
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const selectedLabelProfile = this.resolveLabelProfile(this.userForm.value.labelProfile);
    this.userService.update({
      id: this.user.id,
      name: this.userForm.value.name,
      defaultOrgId: this.userForm.value.org?.id,
      defaultWarehouseId: this.userForm.value.warehouse?.id,
      thermalPrinter: this.userForm.value.thermalPrinter,
      defaultLabelProfileId: selectedLabelProfile?.profileId ?? null,
      defaultLabelProfileCode: selectedLabelProfile?.profileCode ?? null,
      therminal: this.userForm.value.therminal,
      fiscal_id: this.userForm.value.fiscal?.id ?? null,
      catalogueOpenFilter: this.userForm.value.catalogueOpenFilter,
      catalogueOpenFolder: this.userForm.value.catalogueOpenFolder
    });
    void this.saveRingostatSettings();
  }

  get ringostatDestinationType(): RingostatDestinationType {
    return (this.userForm.value.ringostatDestinationType || 'sip') as RingostatDestinationType;
  }

  get ringostatDestinationLabel(): string {
    if (this.ringostatDestinationType === 'phone') return 'Номер для переадресации';
    if (this.ringostatDestinationType === 'schema') return 'ID схемы переадресации';
    return 'SIP account';
  }

  get ringostatAvailabilityStatus(): RingostatAvailabilityStatus {
    return (this.userForm.value.ringostatAvailabilityStatus || 'online') as RingostatAvailabilityStatus;
  }

  get isRingostatTemporaryDnd(): boolean {
    return this.ringostatAvailabilityStatus === 'dnd_temporary';
  }

  private loadFiscals() {
    this.fiscalService.search({ limit: 100, offset: 0 }).subscribe({
      next: (response) => {
        this.fiscalList = response.data || [];
        this.ensureFiscalOption(this.user?.fiscal);
      },
      error: (error) => {
        console.error(error);
        this.fiscalList = [];
        this.ensureFiscalOption(this.user?.fiscal);
      }
    });
  }

  private ensureFiscalOption(fiscal?: Fiscal | null) {
    if (!fiscal?.id) return;
    if (this.fiscalList.some((option) => option.id === fiscal.id)) return;
    this.fiscalList = [fiscal, ...this.fiscalList];
  }

  private async loadRingostatSettings(userId: number) {
    try {
      const settings = await firstValueFrom(this.ringostatSettingsService.getMySettings());
      if (settings.userId !== userId) return;

      this.userForm.patchValue(
        {
          ringostatEnabled: settings.enabled,
          ringostatDestinationType: settings.destinationType || 'sip',
          ringostatDestination: settings.destination || '',
          ringostatSipLogin:
            settings.sipLogin || (settings.destinationType === 'sip' ? settings.destination : '') || '',
          ringostatMobileSipLogin: settings.mobileSipLogin || '',
          ringostatStaffId: settings.staffId || '',
          ringostatFallbackPhone: settings.fallbackPhone || '',
          ringostatDirectPhones: (settings.directPhones || []).join('\n'),
          ringostatAvailabilityStatus: settings.effectiveAvailabilityStatus || settings.availabilityStatus || 'online',
          ringostatDndMinutes: this.getDndMinutesValue(settings.dndUntil)
        },
        { emitEvent: false }
      );
    } catch (error) {
      console.error(error);
    }
  }

  private async saveRingostatSettings() {
    if (!this.user) return;

    this.ringostatSaving = true;
    try {
      await firstValueFrom(this.ringostatSettingsService.saveMySettings(this.buildRingostatPayload()));
      this.notificationService.notice('Ringostat сохранен');
    } catch (error) {
      console.error(error);
      this.notificationService.notice('Ringostat не сохранен');
    } finally {
      this.ringostatSaving = false;
    }
  }

  private buildRingostatPayload(): UpsertRingostatUserSettingsDto {
    const availabilityStatus = this.ringostatAvailabilityStatus;

    return {
      enabled: Boolean(this.userForm.value.ringostatEnabled),
      destinationType: this.ringostatDestinationType,
      destination:
        this.ringostatDestinationType === 'sip'
          ? null
          : this.normalizeString(this.userForm.value.ringostatDestination),
      sipLogin: this.normalizeString(this.userForm.value.ringostatSipLogin),
      mobileSipLogin: this.normalizeString(this.userForm.value.ringostatMobileSipLogin),
      staffId: this.normalizeString(this.userForm.value.ringostatStaffId),
      fallbackPhone: this.normalizeString(this.userForm.value.ringostatFallbackPhone),
      directPhones: this.parseRingostatDirectPhones(this.userForm.value.ringostatDirectPhones),
      availabilityStatus,
      dndMinutes:
        availabilityStatus === 'dnd_temporary'
          ? Number(this.userForm.value.ringostatDndMinutes || 30)
          : null
    };
  }

  private getDndMinutesValue(value: string | Date | null | undefined): number {
    if (!value) return 30;
    const expiresAt = value instanceof Date ? value.getTime() : new Date(value).getTime();
    if (!Number.isFinite(expiresAt)) return 30;

    const minutes = Math.ceil((expiresAt - Date.now()) / 60000);
    if (minutes <= 0) return 30;

    const closestOption = this.ringostatDndMinuteOptions.find((option) => option >= minutes);
    return closestOption || this.ringostatDndMinuteOptions[this.ringostatDndMinuteOptions.length - 1];
  }

  private normalizeString(value: unknown): string | null {
    const normalized = String(value || '').trim();
    return normalized || null;
  }

  private parseRingostatDirectPhones(value: unknown): string[] {
    return String(value || '')
      .split(/[\n,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private async loadPrinterTargets(user?: User, warehouseId?: number) {
    const currentAgentName = user?.defaultThermalPrinter?.trim();
    const currentLabelKey = this.getLabelProfileKey(user?.defaultLabelProfile);
    const effectiveWarehouseId = warehouseId ?? user?.defaultWarehouse?.id;

    try {
      const receiptTargets = await this.thermalPrintTargetService.getSelectableTargets(
        'receipt',
        currentAgentName,
        effectiveWarehouseId
      );
      this.thermalAgentOptions = receiptTargets.map((option) => ({
        agentName: option.agentName || option.targetId,
        label: option.label
      }));
    } catch (error) {
      console.error(error);
      this.thermalAgentOptions = currentAgentName
        ? [
            {
              agentName: currentAgentName,
              label: `${currentAgentName} (офлайн, сохранённый принтер)`
            }
          ]
        : [];
    }

    try {
      const labelProfiles = await firstValueFrom(this.computerService.getLabelProfiles());
      let options = this.mapOptionsFromProfiles(labelProfiles);

      if (!options.length) {
        const labelTargets = await this.thermalPrintTargetService.getSelectableTargets(
          'label',
          undefined,
          effectiveWarehouseId
        );
        options = this.mapOptionsFromTargets(labelTargets);
      }

      this.labelProfileOptions = options;

      if (
        currentLabelKey &&
        !this.labelProfileOptions.some((option) => option.key === currentLabelKey)
      ) {
          this.labelProfileOptions.unshift({
            key: currentLabelKey,
            label: `${currentLabelKey} (сохранённый профиль)`,
            profileCode: currentLabelKey
          });
      }
    } catch (error) {
      console.error(error);
      this.labelProfileOptions = currentLabelKey
        ? [
            {
              key: currentLabelKey,
              label: `${currentLabelKey} (офлайн, сохранённый профиль)`,
              profileCode: currentLabelKey
            }
          ]
        : [];
    }
  }

  private mapOptionsFromProfiles(labelProfiles: LabelProfileRef[]): PrinterTargetOption[] {
    const seen = new Set<string>();
    return labelProfiles
        .map((profile) => ({
          key: String(profile.code || '').trim() || String(profile.id),
          label:
            String(profile.name || '').trim() ||
            String(profile.code || '').trim() ||
            `Профиль ${profile.id}`,
          profileId: profile.id,
          profileCode: profile.code || null
        }))
      .filter((option) => {
        if (!option.key || seen.has(option.key)) {
          return false;
        }
        seen.add(option.key);
        return true;
      });
  }

  private mapOptionsFromTargets(targets: PrinterTarget[]): PrinterTargetOption[] {
    const seen = new Set<string>();
    return targets
      .map((target) => {
        const profileId = target.profileId ?? target.profile?.profileId ?? target.profile?.id ?? null;
        const profileCode =
          target.profileCode ?? target.profile?.profileCode ?? target.profile?.code ?? null;
        const profileName =
          target.profileName ?? target.profile?.profileName ?? target.profile?.name ?? null;
        const key = String(profileCode || '').trim() || (profileId !== null ? String(profileId) : '');

        return {
          key,
          label: String(profileName || '').trim() || String(profileCode || '').trim() || '',
          profileId,
          profileCode
        };
      })
      .filter((option) => {
        if (!option.key || !option.label || seen.has(option.key)) {
          return false;
        }
        seen.add(option.key);
        return true;
      });
  }

  private resolveLabelProfile(key: unknown) {
    const lookup = String(key || '').trim();
    if (!lookup) return undefined;
    return this.labelProfileOptions.find((option) => option.key === lookup);
  }

  private getLabelProfileKey(profile?: PrinterTarget | null) {
    if (!profile) return '';
    return (
      profile.profileCode?.trim() ||
      profile.code?.trim() ||
      (profile.profileId !== undefined && profile.profileId !== null
        ? String(profile.profileId)
        : '') ||
      String(profile.id || '')
    );
  }
}
